import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LayoutContainer from 'components/Common/LayoutContainer'
import { useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { breakpointSizes, mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import { LOYALTY_CREDIT_CARD_LAYOUT_CONTAINER_CLASS } from './loyaltyCreditCardConstants'
import { LoyaltyCreditCardCreamSection, loyaltyCreditCardWrappingLabel } from './components/loyaltyCreditCardStyles'
import LoyaltyCreditCardApplyNowButton from './components/LoyaltyCreditCardApplyNowButton'
import { LOYALTY_CREDIT_CARD_APPLY_NOW_PLACEMENTS } from 'analytics/snowplow/events/loyaltyCreditCardEvents'
import Image from 'components/Common/Image'
import type { ImageMediaSource } from 'components/Common/Image/Image'

const EARN_IMAGE_ID = 'earn-everyday-v3'

/* The image is half of the 1172px container from tablet up, and full width below it. Both ask
   for 3x: at 2x a retina screen paints one image pixel per device pixel and the photo aliases. */
const DESKTOP_IMAGE_WIDTH = 586
const MOBILE_IMAGE_WIDTH = 512

const EARN_IMAGE_SOURCES: Array<ImageMediaSource> = [
  {
    media: `(min-width: ${breakpointSizes.tablet.min}px)`,
    id: EARN_IMAGE_ID,
    width: DESKTOP_IMAGE_WIDTH,
    dpr: 3,
  },
]

const SplitSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(28)};
  align-items: center;

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    gap: ${rem(60)};
    align-items: center;
  }
`

const SplitImage = styled.div`
  width: 100%;
  border-radius: ${rem(8)};
  overflow: hidden;
  flex-shrink: 0;
  aspect-ratio: 335 / 225;

  ${mediaQueryUp.tablet} {
    width: 50%;
    aspect-ratio: 570 / 372;
  }

  picture {
    display: block;
    height: 100%;
    width: 100%;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`

const SplitText = styled.div`
  flex: 1;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};

  ${mediaQueryDown.mobile} {
    padding-inline: ${rem(12)};
  }

  ${mediaQueryUp.tablet} {
    max-width: ${rem(415)};
  }
`

const EarnBodyText = styled(BodyText)`
  && {
    padding: 0;
  }
`

const EarnCtaGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(8)};

  ${mediaQueryUp.desktop} {
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: ${rem(8)};
    width: 100%;
    /* Both buttons fit side by side in English. Longer labels take a second
       row rather than running off the edge of the text column. */
    flex-wrap: wrap;
  }
`

const ApplyButton = styled(LoyaltyCreditCardApplyNowButton)`
  && {
    min-width: ${rem(148)};

    ${loyaltyCreditCardWrappingLabel}
  }
`

const EarnLearnMoreButton = styled(TextButton)`
  && {
    ${loyaltyCreditCardWrappingLabel}
  }
`

function LoyaltyCreditCardEarnSection() {
  const intl = useIntl()
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)

  return (
    <LoyaltyCreditCardCreamSection background="light-background" id="overview">
      <LayoutContainer className={LOYALTY_CREDIT_CARD_LAYOUT_CONTAINER_CLASS} variant="large">
        <SplitSection>
          <SplitImage>
            <Image
              id={EARN_IMAGE_ID}
              sources={EARN_IMAGE_SOURCES}
              width={MOBILE_IMAGE_WIDTH}
              dpr={3}
              fit="center"
              objectFit="cover"
            />
          </SplitImage>
          <SplitText>
            <Heading variant="heading1" align="center" tabletAlign="start">
              {intl.formatMessage(
                {
                  id: '2uBXvJ3jkjV_-Rf1R4NY',
                  defaultMessage: 'Earn {italic} with everyday<lineBreak></lineBreak>spending',
                  description: 'Section heading for earning points with everyday spending on the Société Credit Card page',
                },
                {
                  italic: (
                    <i>
                      {intl.formatMessage(
                        {
                          id: 'ntQbMTlvQ-WC0UzTvtS5',
                          defaultMessage: 'Société Points<lineBreak></lineBreak>faster',
                          description: 'Italic emphasis text in the earn points heading on the Société Credit Card page',
                        },
                        {
                          lineBreak: () => <br key="earn-heading-points-line-break" />,
                        },
                      )}
                    </i>
                  ),
                  lineBreak: () => <br key="earn-heading-everyday-line-break" />,
                },
              )}
            </Heading>
            <EarnBodyText variant="medium">
              {intl.formatMessage({
                id: '9amVoVvClYQTEX9G6udT',
                defaultMessage: 'Every cup of coffee, trip to the supermarket and dinner out earns you points with the Société Credit Card — turning the spending you do every day into the escapes you dream about. Plus, when you book with Luxury Escapes, you earn even more.',
                description: 'Section body – earn points section on Société Credit Card page',
              })}
            </EarnBodyText>
            <EarnBodyText variant="medium">
              {intl.formatMessage({
                id: '2A_iJWijhFhB5NCFL64e',
                defaultMessage: 'Use your Société Points like cash towards hotels, flights, tours or cruises.',
                description: 'Second body paragraph in the earn points section on the Société Credit Card page',
              })}
            </EarnBodyText>
            <EarnCtaGroup>
              <ApplyButton fit="mobile-full-width" placement={LOYALTY_CREDIT_CARD_APPLY_NOW_PLACEMENTS.earn} />
              <EarnLearnMoreButton kind="tertiary" size="large" to={`/${regionCode.toLowerCase()}/societe`} target="_blank">
                {intl.formatMessage({
                  id: 'Olhq9Ehg2yAnnzDSju9L',
                  defaultMessage: 'Learn more about Société',
                  description: 'Secondary call to action linking to the Société program page from the credit card page',
                })}
              </EarnLearnMoreButton>
            </EarnCtaGroup>
          </SplitText>
        </SplitSection>
      </LayoutContainer>
    </LoyaltyCreditCardCreamSection>
  )
}

export default LoyaltyCreditCardEarnSection
